import { useState, useEffect, useCallback } from 'react';

export const useEasterEggs = (onEasterEggTriggered: (type: string) => void) => {
  const [, setKonamiCode] = useState<number[]>([]);
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  // Handle Konami code
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Konami code sequence: ↑↑↓↓←→←→BA
    const konamiSequence = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65];
    
    setKonamiCode(prev => {
      const newCode = [...prev, e.keyCode];
      
      // Keep only the last 10 keys
      if (newCode.length > konamiSequence.length) {
        newCode.shift();
      }
      
      // Check if sequence matches
      if (newCode.join(',') === konamiSequence.join(',')) {
        onEasterEggTriggered('konami');
        return [];
      }
      
      return newCode;
    });
  }, [onEasterEggTriggered]);

  // Handle rapid clicks on logo/title
  const handleTitleClick = useCallback(() => {
    const now = Date.now();
    
    if (now - lastClickTime < 500) { // Within 500ms
      setClickCount(prev => prev + 1);
      
      if (clickCount >= 4) { // 5 rapid clicks
        onEasterEggTriggered('rapidClick');
        setClickCount(0);
      }
    } else {
      setClickCount(1);
    }
    
    setLastClickTime(now);
  }, [clickCount, lastClickTime, onEasterEggTriggered]);

  // Handle special text inputs
  const handleTextEasterEgg = useCallback((text: string) => {
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('neodia rocks') || lowerText.includes('neodia is awesome')) {
      onEasterEggTriggered('specialText');
    }
    
    if (lowerText === 'debug mode') {
      onEasterEggTriggered('debugMode');
    }
  }, [onEasterEggTriggered]);

  // Check for time-based easter eggs
  const checkTimeEasterEggs = useCallback(() => {
    const now = new Date();
    const hour = now.getHours();
    const month = now.getMonth();
    const day = now.getDate();
    
    // Midnight easter egg
    if (hour === 0) {
      onEasterEggTriggered('midnight');
    }
    
    // Christmas easter egg (December 25)
    if (month === 11 && day === 25) {
      onEasterEggTriggered('christmas');
    }
    
    // Halloween easter egg (October 31)
    if (month === 9 && day === 31) {
      onEasterEggTriggered('halloween');
    }
  }, [onEasterEggTriggered]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    
    // Check time-based easter eggs on mount
    checkTimeEasterEggs();
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, checkTimeEasterEggs]);

  return {
    handleTitleClick,
    handleTextEasterEgg
  };
};

export const getEasterEggMessage = (type: string): string => {
  switch (type) {
    case 'konami':
      return '🎉 Konami Code activated! You found the secret! Premium features unlocked! 🎉';
    case 'rapidClick':
      return '👆 Wow, you really love clicking! Here\'s a secret: Your ad will get 2x more views! 👆';
    case 'specialText':
      return '💖 Aww, thank you! We love you too! Your ad just got priority placement! 💖';
    case 'debugMode':
      return '🔧 Debug mode activated! You can now see all the behind-the-scenes magic! 🔧';
    case 'midnight':
      return '🌙 Midnight advertiser! Night owls get 50% off their next ad! 🌙';
    case 'christmas':
      return '🎄 Merry Christmas! Your ad gets festive sparkles and holiday boost! 🎄';
    case 'halloween':
      return '🎃 Happy Halloween! Your ad gets spooky powers and 3x reach! 🎃';
    default:
      return '✨ You found a secret! Something special is happening... ✨';
  }
}; 